import { useState, useCallback } from 'react';

interface ValidationRules {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: string) => string | null;
}

interface FieldValidation {
  [key: string]: ValidationRules;
}

interface ValidationErrors {
  [key: string]: string;
}

export const useFormValidation = (validationRules: FieldValidation) => {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    const phoneRegex = /^[\+]?[0-9]{7,15}$/;
    return phoneRegex.test(cleanPhone);
  };

  const validateName = (name: string): boolean => {
    const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]{2,}$/;
    return nameRegex.test(name);
  };

  const sanitizeInput = (input: string): string => {
    return input
      .trim()
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  };

  const validateField = useCallback(
    (fieldName: string, value: string): string | null => {
      const rules = validationRules[fieldName];
      if (!rules) return null;

      const sanitizedValue = sanitizeInput(value);

      if (rules.required && !sanitizedValue) {
        return 'Este campo es requerido';
      }

      if (rules.minLength && sanitizedValue.length < rules.minLength) {
        return `Debe tener al menos ${rules.minLength} caracteres`;
      }

      if (rules.maxLength && sanitizedValue.length > rules.maxLength) {
        return `No puede exceder ${rules.maxLength} caracteres`;
      }

      if (rules.pattern && !rules.pattern.test(sanitizedValue)) {
        return 'Formato inválido';
      }

      if (rules.custom) {
        return rules.custom(sanitizedValue);
      }

      return null;
    },
    [validationRules]
  );

  const handleBlur = useCallback((fieldName: string) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  const handleChange = useCallback(
    (fieldName: string, value: string) => {
      if (touched[fieldName]) {
        const error = validateField(fieldName, value);
        setErrors((prev) => ({
          ...prev,
          [fieldName]: error || '',
        }));
      }
    },
    [touched, validateField]
  );

  const validateAll = useCallback(
    (formData: { [key: string]: string }): boolean => {
      const newErrors: ValidationErrors = {};
      let isValid = true;

      Object.keys(validationRules).forEach((fieldName) => {
        const error = validateField(fieldName, formData[fieldName] || '');
        if (error) {
          newErrors[fieldName] = error;
          isValid = false;
        }
      });

      setErrors(newErrors);
      setTouched(
        Object.keys(validationRules).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {}
        )
      );

      return isValid;
    },
    [validationRules, validateField]
  );

  const resetValidation = useCallback(() => {
    setErrors({});
    setTouched({});
  }, []);

  return {
    errors,
    touched,
    validateEmail,
    validatePhone,
    validateName,
    sanitizeInput,
    validateField,
    handleBlur,
    handleChange,
    validateAll,
    resetValidation,
  };
};
